import { useAtom } from 'jotai'
import { useCallback } from 'react'
import { useDeleteBalancesCache } from '@/src/hooks/balances/use-delete-balances-cache'
import {
  animatedOpenAtom,
  filteredNetworkAtom,
  networkSelectionMetadataAtom,
  networkSelectionOpenAtom,
  type TokenSelectionMetadata,
  tokenSelectionOpenAtom,
  yourBalancesAccordionAtom,
} from './atoms'

export const useTokenSelection = () => {
  const [tokenSelectionOpen, setTokenSelectionOpen] = useAtom(
    tokenSelectionOpenAtom,
  )
  const [filteredNetwork, setFilteredNetwork] = useAtom(filteredNetworkAtom)
  const [accordionValue, setAccordionValue] = useAtom(yourBalancesAccordionAtom)
  const { mutate } = useDeleteBalancesCache()

  const [isAnimatedOpen, setIsAnimatedOpen] = useAtom(animatedOpenAtom)

  const open = useCallback(
    (direction: 'from' | 'to') => {
      if (direction === 'to') {
        setAccordionValue('')
      }
      setTokenSelectionOpen({ open: true, direction })
      setIsAnimatedOpen(true)

      if (direction === 'from') {
        mutate()
      }
    },
    [setAccordionValue, setIsAnimatedOpen, setTokenSelectionOpen, mutate],
  )

  const close = useCallback(() => {
    setTokenSelectionOpen({ open: false, direction: null })
    setIsAnimatedOpen(false)
    setFilteredNetwork(null)
  }, [setTokenSelectionOpen, setIsAnimatedOpen, setFilteredNetwork])

  return {
    isOpen: tokenSelectionOpen.open,
    direction: tokenSelectionOpen.direction,
    open,
    close,
    filteredNetwork,
    setFilteredNetwork,
    accordionValue,
    setAccordionValue,
    isAnimatedOpen,
    setIsAnimatedOpen,
  }
}

export const useNetworkSelection = () => {
  const [networkSelectionOpen, setNetworkSelectionOpen] = useAtom(
    networkSelectionOpenAtom,
  )
  const [networkSelectionMetadata, setNetworkSelectionMetadata] = useAtom(
    networkSelectionMetadataAtom,
  )

  const [, setIsAnimatedOpen] = useAtom(animatedOpenAtom)

  const open = useCallback(
    (
      metadata: Partial<TokenSelectionMetadata> & {
        direction?: 'from' | 'to' | null
      },
    ) => {
      setNetworkSelectionOpen({
        open: true,
        direction: metadata.direction ?? 'from',
      })
      setNetworkSelectionMetadata(metadata)
      setIsAnimatedOpen(true)
    },
    [setNetworkSelectionOpen, setNetworkSelectionMetadata, setIsAnimatedOpen],
  )

  const close = useCallback(() => {
    setNetworkSelectionOpen({ open: false, direction: null })
    setNetworkSelectionMetadata(null)
  }, [setNetworkSelectionOpen, setNetworkSelectionMetadata])

  return {
    isOpen: networkSelectionOpen.open,
    direction: networkSelectionOpen.direction,
    open,
    close,
    metadata: networkSelectionMetadata,
  }
}
